"use client";

import type { MouseEvent, ReactNode } from "react";
import Link from "next/link";
import { cn } from "@/lib/utils";
import styles from "./head-tabs.module.css";

export type HeadTabItem<K extends string> = {
  key: K;
  label: string;
  /** The tab's own address: a page, or this page with `?tab=`, so it can be shared and opened in a new tab. */
  href: string;
  icon?: ReactNode;
  /** A count in a chip, when there is one worth printing. */
  count?: string;
  /** A small chip after the label, for a tab that is not open yet (a lock and the time it opens). */
  hint?: ReactNode;
  /** Something new behind this tab; `dotLabel` says what to a screen reader. */
  dot?: boolean;
  dotLabel?: string;
  /** A tab that is another page (the contest's Rules): always followed, never switched in place. */
  follow?: boolean;
};

/**
 * A dark head's sections as tabs on its bottom edge (`EX-CPG02`, Wave 30
 * decision 5, `docs/03` §6): the contest page and the Judge leaderboards.
 *
 * `edge` sits flush on the head's bottom edge, and the open tab is the page's
 * light ground (`bg-neutral-50`, the Judge shell's) running up into the head
 * with curved corners, so the head and the page read as one piece. `compact`
 * is the same tabs as a pill track for a dark bar (the contest page's sticky
 * bar once the head has scrolled away).
 *
 * Every tab is a link. A page that changes tabs in place (the contest page)
 * passes `onSelect`: a plain click then switches without a navigation, and a
 * click with a modifier or the middle button still opens the address. That
 * keeps every section shareable without a raw `<button>` (`AGENTS.md` rule 17).
 */
export function HeadTabs<K extends string>({
  items,
  active,
  label,
  variant = "edge",
  onSelect,
  className,
}: {
  items: ReadonlyArray<HeadTabItem<K>>;
  active: K;
  label: string;
  variant?: "edge" | "compact";
  onSelect?: (key: K) => void;
  className?: string;
}) {
  const compact = variant === "compact";

  function handleClick(event: MouseEvent<HTMLAnchorElement>, item: HeadTabItem<K>) {
    if (!onSelect || item.follow) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    onSelect(item.key);
  }

  return (
    <nav
      aria-label={label}
      className={cn(
        "flex overflow-x-auto [scrollbar-width:none]",
        compact ? "gap-0.5 rounded-xl bg-white/[0.06] p-1" : "items-end gap-1 px-1",
        className,
      )}
    >
      {items.map((item) => {
        const current = item.key === active;
        return (
          <Link
            key={item.key}
            href={item.href}
            prefetch={onSelect && !item.follow ? false : undefined}
            scroll={onSelect && !item.follow ? false : undefined}
            aria-current={current ? "page" : undefined}
            onClick={(event) => handleClick(event, item)}
            className={cn(
              "relative inline-flex shrink-0 items-center gap-2 whitespace-nowrap transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-peach",
              compact
                ? [
                    "h-9 rounded-[9px] px-3 text-sm",
                    current ? "bg-neutral-50 font-bold text-neutral-900" : "font-medium text-neutral-300 hover:text-white",
                  ]
                : [
                    "h-[50px] rounded-t-[14px] px-4 text-[14.5px] sm:px-5",
                    current
                      ? cn(styles.open, "bg-neutral-50 font-bold text-neutral-900")
                      : "font-medium text-neutral-300 hover:bg-white/[0.07] hover:text-white",
                  ],
            )}
          >
            {item.icon ? (
              <span aria-hidden className={cn("inline-flex", current ? "text-primary" : null)}>
                {item.icon}
              </span>
            ) : null}
            {item.label}
            {/* Spaces between the parts, so the link's name reads "Problems 2", not "Problems2". */}
            {item.hint ? " " : null}
            {item.hint ? (
              <span
                className={cn(
                  "inline-flex h-[22px] items-center gap-1 rounded-full border px-2 font-mono text-[11.5px] font-medium",
                  current ? "border-neutral-200 text-neutral-500" : "border-white/15 text-neutral-400",
                )}
              >
                {item.hint}
              </span>
            ) : null}
            {item.count !== undefined ? " " : null}
            {item.count !== undefined ? (
              <span
                className={cn(
                  "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 font-mono text-[11.5px] font-bold tabular-nums",
                  current ? "bg-neutral-100 text-neutral-500" : "bg-white/10 text-neutral-200",
                )}
              >
                {item.count}
              </span>
            ) : null}
            {item.dot ? " " : null}
            {item.dot ? (
              <>
                <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-primary" />
                {item.dotLabel ? <span className="sr-only">{item.dotLabel}</span> : null}
              </>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}
